import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, Globe, LogOut, Pencil, Sparkles, Users, ChevronRight } from 'lucide-react';
import {
  useAffiliateOverview,
  useLogout,
  useMe,
  useProfile,
  useUpdateLanguage,
  useUpdateNickname,
} from '../api/hooks';
import {
  Avatar,
  Button,
  Cell,
  PageHeader,
  Section,
  Segmented,
  TextInput,
} from '../components/ui';
import { getTg } from '../lib/tg';
import {
  type Locale,
  localeToNeburstLang,
  setLocale,
  useLocale,
  useT,
} from '../i18n';

export function Profile() {
  const navigate = useNavigate();
  const t = useT();
  const locale = useLocale();
  const me = useMe();
  const profile = useProfile();
  const updateNick = useUpdateNickname();
  const updateLang = useUpdateLanguage();
  const aff = useAffiliateOverview();
  const logout = useLogout();

  const data = (profile.data?.data ?? {}) as Record<string, unknown>;
  const nickname = String(data.nickname ?? me.data?.nickname ?? me.data?.email?.split('@')[0] ?? '');
  const email = String(data.email ?? me.data?.email ?? '');
  const tgUser = getTg()?.initDataUnsafe?.user;
  const tgPhotoUrl = tgUser?.photo_url;

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(nickname);
  const [editError, setEditError] = useState<string | null>(null);

  const affData = extractAff(aff.data);

  const onPickLocale = (l: Locale) => {
    setLocale(l);
    updateLang.mutate(localeToNeburstLang(l));
  };

  const doLogout = () => {
    const tg = getTg();
    const fire = () => logout.mutate();
    tg ? tg.showConfirm(t('profile.confirmLogout'), (ok) => ok && fire()) : fire();
  };

  return (
    <>
      <PageHeader title={t('profile.title')} />

      <Section>
        <div
          style={{
            padding: '22px 18px',
            display: 'flex',
            alignItems: 'center',
            gap: 16,
            borderBottom: '0.5px solid var(--tg-separator)',
          }}
        >
          <Avatar
            label={nickname || email}
            size={64}
            src={tgPhotoUrl}
          />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                fontSize: 22,
                fontWeight: 700,
                letterSpacing: '-0.02em',
                lineHeight: 1.2,
                color: 'var(--tg-text)',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {nickname || t('profile.nicknameEmpty')}
            </div>
            {email && (
              <div
                style={{
                  fontSize: 13.5,
                  color: 'var(--tg-hint)',
                  marginTop: 4,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  fontWeight: 400,
                }}
              >
                {email}
              </div>
            )}
          </div>
        </div>
        <Cell
          leading={<IconBg color="#5856D6"><Bell size={14} color="#fff" /></IconBg>}
          title={t('profile.alerts')}
          subtitle={t('profile.alertsDesc')}
          onClick={() => navigate('/me/alerts')}
        />
        <div
          style={{
            padding: '12px 16px 14px',
            display: 'flex',
            flexDirection: 'column',
            gap: 10,
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
            }}
          >
            <IconBg color="#FF9500">
              <Globe size={14} color="#fff" />
            </IconBg>
            <span style={{ fontSize: 15.5, fontWeight: 500 }}>{t('profile.language')}</span>
          </div>
          <Segmented<Locale>
            value={locale}
            onChange={onPickLocale}
            options={[
              { value: 'zh', label: '中文' },
              { value: 'en', label: 'English' },
            ]}
          />
        </div>
      </Section>

      <Section title={t('profile.affiliate')}>
        <Cell
          leading={<IconBg color="#34C759"><Users size={14} color="#fff" /></IconBg>}
          title={t('profile.myInvites')}
          subtitle={t('profile.invitesSummary', {
            count: affData.invitedCount ?? 0,
            commission: affData.totalCommission ?? t('common.dash'),
          })}
          trailing={<ChevronRight size={18} color="var(--tg-hint)" />}
          last
        />
      </Section>

      <Section title={t('profile.about')}>
        <Cell
          leading={<IconBg color="#007AFF"><Sparkles size={14} color="#fff" /></IconBg>}
          title={t('profile.version')}
          trailing="0.2.0"
          last
        />
      </Section>

      <div style={{ padding: '16px' }}>
        <Button variant="danger" leading={<LogOut size={16} />} onClick={doLogout} loading={logout.isPending}>
          {t('profile.logout')}
        </Button>
      </div>
    </>
  );
}

function IconBg({ color, children }: { color: string; children: React.ReactNode }) {
  return (
    <div
      style={{
        width: 26,
        height: 26,
        borderRadius: 8,
        background: color,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {children}
    </div>
  );
}

function extractAff(data: unknown): {
  invitedCount?: number;
  totalCommission?: string;
} {
  if (!data || typeof data !== 'object') return {};
  const obj = data as Record<string, unknown>;
  const inner = (obj.data as Record<string, unknown> | undefined) ?? obj;
  const invited =
    typeof inner.invited_count === 'number'
      ? inner.invited_count
      : typeof inner.total_invites === 'number'
        ? inner.total_invites
        : undefined;
  const commission =
    typeof inner.total_commission === 'number'
      ? `$${(inner.total_commission as number).toFixed(2)}`
      : typeof inner.commission === 'number'
        ? `$${(inner.commission as number).toFixed(2)}`
        : undefined;
  return { invitedCount: invited, totalCommission: commission };
}
